import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar, 
  ChevronDown, 
  Plus, 
  Trash2, 
  Search,
  X,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  Clock,
  Play,
  RefreshCw,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];

// Helper to get working days for a month with rollover
const getWorkingDaysGrid = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Get the Monday of the week containing the 1st
  const firstDayOfWeek = firstDay.getDay(); // 0=Sun, 1=Mon, ...
  const startOffset = firstDayOfWeek === 0 ? -6 : 1 - firstDayOfWeek;
  const gridStart = new Date(year, month, 1 + startOffset);
  
  // Build 4 weeks (20 working days)
  const weeks = [];
  let currentDate = new Date(gridStart);
  
  for (let week = 0; week < 4; week++) {
    const weekDays = [];
    for (let day = 0; day < 5; day++) {
      weekDays.push({
        date: new Date(currentDate),
        dayOfMonth: currentDate.getDate(),
        isCurrentMonth: currentDate.getMonth() === month,
        isToday: currentDate.toDateString() === new Date().toDateString(),
        weekNumber: week + 1,
        dayOfWeek: day
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    // Skip weekend
    currentDate.setDate(currentDate.getDate() + 2);
    weeks.push(weekDays);
  }
  
  return weeks;
};

const PICountSchedule = ({ isOpen, onClose }) => {
  const [schedules, setSchedules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [organized, setOrganized] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingCounts, setCreatingCounts] = useState(false);
  
  // Calendar state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  
  // Modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showCellModal, setShowCellModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [creatingCountFromSchedule, setCreatingCountFromSchedule] = useState(false);
  
  // Supplier groups from database
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplierGroups, setSelectedSupplierGroups] = useState([]);
  const [viewAll, setViewAll] = useState(false);
  
  // Free stock status
  const [freeStockUpdatedToday, setFreeStockUpdatedToday] = useState(false);
  const [todaySchedule, setTodaySchedule] = useState(null);
  
  // Schedule status (auto-creation info)
  const [scheduleStatus, setScheduleStatus] = useState(null);
  
  // Auto-run toggle state
  const [autoRunEnabled, setAutoRunEnabled] = useState(true);
  const [showAutoRunConfirmModal, setShowAutoRunConfirmModal] = useState(false);

  // Get all supplier group IDs that are already scheduled
  const scheduledSupplierGroupIds = useMemo(() => {
    const ids = new Set();
    schedules.forEach(schedule => {
      (schedule.supplier_group_ids || []).forEach(id => ids.add(id));
    });
    return ids;
  }, [schedules]);

  useEffect(() => {
    if (isOpen) {
      loadSchedule();
      loadSupplierGroups();
      checkTodaySchedule();
      loadScheduleStatus();
    }
  }, [isOpen]);

  const loadSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pi-counts/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSchedules(data.schedules || []);
      setGroups(data.groups || []);
      setOrganized(data.organized || null);
    } catch (error) {
      console.error('Error loading schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const loadSupplierGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use manual-supplier-groups endpoint with include_products for searching
      const response = await fetch(`${API_URL}/api/stock-database/manual-supplier-groups?include_products=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      // Response format is { groups: [...] }
      const groups = data.groups || data.suppliers || data.supplier_groups || (Array.isArray(data) ? data : []);
      setSupplierGroups(groups);
    } catch (error) {
      console.error('Error loading supplier groups:', error);
    }
  };

  const checkTodaySchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pi-counts/schedule/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTodaySchedule(data);
      setFreeStockUpdatedToday(data.free_stock_updated_today);
    } catch (error) {
      console.error('Error checking today schedule:', error);
    }
  };
  
  const loadScheduleStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pi-counts/schedule/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setScheduleStatus(data);
      // Load auto-run enabled state from status
      if (data.auto_run_enabled !== undefined) {
        setAutoRunEnabled(data.auto_run_enabled);
      }
    } catch (error) {
      console.error('Error loading schedule status:', error);
    }
  };
  
  const toggleAutoRun = async (enable) => {
    if (enable && !autoRunEnabled) {
      // Turning ON - show confirmation modal
      setShowAutoRunConfirmModal(true);
    } else if (!enable && autoRunEnabled) {
      // Turning OFF - just disable
      await saveAutoRunSetting(false);
    }
  };
  
  const saveAutoRunSetting = async (enabled, runNow = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pi-counts/schedule/auto-run`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled, run_now: runNow })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setAutoRunEnabled(enabled);
        if (enabled) {
          if (runNow && data.counts_created > 0) {
            toast.success(`Auto-run enabled. Created ${data.counts_created} counts for today.`);
          } else if (runNow) {
            toast.success('Auto-run enabled. No counts to create today.');
          } else {
            toast.success('Auto-run enabled. Will run on next working day at 6 AM GMT.');
          }
        } else {
          toast.info('Auto-run disabled. Manual trigger required for scheduled counts.');
        }
        // Refresh status
        await loadScheduleStatus();
        await checkTodaySchedule();
      } else {
        toast.error(data.detail || 'Failed to update auto-run setting');
      }
    } catch (error) {
      console.error('Error saving auto-run setting:', error);
      toast.error('Failed to update auto-run setting');
    }
    setShowAutoRunConfirmModal(false);
  };

  // Filter supplier groups based on search and viewAll toggle
  const filteredSupplierGroups = useMemo(() => {
    let filtered = supplierGroups;
    
    // Filter by search query - searches name, category, prefixes, product codes, and product names
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sg => {
        // Search by group name
        if (sg.name?.toLowerCase().includes(query)) return true;
        
        // Search by category
        if (sg.category?.toLowerCase().includes(query)) return true;
        
        // Search by prefixes (product analysis group names)
        if (sg.prefixes?.some(p => p.toLowerCase().includes(query))) return true;
        
        // Search by product codes
        if (sg.sample_products?.some(p => p.code?.toLowerCase().includes(query))) return true;
        
        // Search by product names
        if (sg.sample_products?.some(p => p.name?.toLowerCase().includes(query))) return true;
        
        return false;
      });
    }
    
    // Filter out already scheduled groups (unless viewAll is true)
    if (!viewAll) {
      filtered = filtered.filter(sg => !scheduledSupplierGroupIds.has(sg.id));
    }
    
    return filtered;
  }, [supplierGroups, searchQuery, viewAll, scheduledSupplierGroupIds]);

  // Filter by category for the cell modal
  const getFilteredByCategory = (category) => {
    return filteredSupplierGroups.filter(sg => 
      sg.category?.toLowerCase() === category?.toLowerCase()
    );
  };

  const handleCellClick = (category, week, day, dateInfo = null) => {
    setSelectedCell({ category, week, day, dateInfo });
    
    // Find existing entry for this cell (using week/day pattern)
    const existing = schedules.find(s => 
      s.category === category && 
      s.week_number === week && 
      s.day_of_week === day
    );
    
    if (existing) {
      setSelectedSupplierGroups(existing.supplier_group_ids || []);
    } else {
      setSelectedSupplierGroups([]);
    }
    
    setSearchQuery('');
    setViewAll(false);
    setShowCellModal(true);
  };

  // Show edit type modal before saving
  const handleSaveCellWithType = () => {
    if (!selectedCell || selectedSupplierGroups.length === 0) {
      toast.error('Please select at least one supplier group');
      return;
    }
    setPendingEdit({ type: 'save', cell: selectedCell, groups: selectedSupplierGroups });
    setShowEditTypeModal(true);
  };

  const handleSaveCell = async (applyToAll = true) => {
    if (!selectedCell) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Get prefixes from selected supplier groups
      const prefixes = supplierGroups
        .filter(sg => selectedSupplierGroups.includes(sg.id))
        .flatMap(sg => sg.prefixes || [])
        .filter((v, i, a) => a.indexOf(v) === i);
      
      const response = await fetch(`${API_URL}/api/pi-counts/schedule/entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category: selectedCell.category,
          week_number: selectedCell.week,
          day_of_week: selectedCell.day,
          supplier_group_ids: selectedSupplierGroups,
          prefixes: prefixes,
          apply_to_all: applyToAll,
          one_off_date: !applyToAll && selectedCell.dateInfo ? selectedCell.dateInfo.date.toISOString().split('T')[0] : null
        })
      });
      
      if (response.ok) {
        toast.success(applyToAll ? 'Schedule updated for all months' : 'One-off schedule created');
        loadSchedule();
        setShowCellModal(false);
        setShowEditTypeModal(false);
      } else {
        toast.error('Failed to update schedule');
      }
    } catch (error) {
      console.error('Error saving cell:', error);
      toast.error('Failed to save schedule');
    }
  };

  const handleDeleteCell = async (applyToAll = true) => {
    if (!selectedCell) return;
    
    try {
      const token = localStorage.getItem('token');
      const oneOffParam = !applyToAll && selectedCell.dateInfo 
        ? `&one_off_date=${selectedCell.dateInfo.date.toISOString().split('T')[0]}` 
        : '';
      const response = await fetch(
        `${API_URL}/api/pi-counts/schedule/entry?category=${selectedCell.category}&week_number=${selectedCell.week}&day_of_week=${selectedCell.day}${oneOffParam}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success(applyToAll ? 'Schedule entry deleted for all months' : 'One-off entry removed');
        loadSchedule();
        setShowCellModal(false);
        setShowEditTypeModal(false);
      }
    } catch (error) {
      console.error('Error deleting cell:', error);
      toast.error('Failed to delete schedule entry');
    }
  };
  
  // Create count from schedule - creates combined count with all selected supplier groups
  const handleCreateCountFromSchedule = async () => {
    if (!selectedCell || selectedSupplierGroups.length === 0) {
      toast.error('No supplier groups selected');
      return;
    }
    
    if (!freeStockUpdatedToday) {
      toast.error('Please upload today\'s Free Stock Report first');
      return;
    }
    
    try {
      setCreatingCountFromSchedule(true);
      const token = localStorage.getItem('token');
      
      // Determine the target date - use selected date or today
      const targetDate = selectedCell.dateInfo?.date 
        ? selectedCell.dateInfo.date.toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/pi-counts/schedule/create-count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category: selectedCell.category,
          week_number: selectedCell.week,
          day_of_week: selectedCell.day,
          date: targetDate
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const isCombined = data.count?.is_combined;
        const groupCount = data.count?.supplier_groups?.length || 1;
        toast.success(
          isCombined 
            ? `Combined count created with ${groupCount} supplier groups` 
            : 'Count created successfully'
        );
        setShowCellModal(false);
        
        // Navigate to the new count
        if (data.count?.id) {
          window.location.href = `/pi-counts/${data.count.id}`;
        }
      } else {
        toast.error(data.detail || 'Failed to create count');
      }
    } catch (error) {
      console.error('Error creating count from schedule:', error);
      toast.error('Failed to create count');
    } finally {
      setCreatingCountFromSchedule(false);
    }
  };

  const toggleSupplierGroup = (groupId) => {
    setSelectedSupplierGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleCreateTodaysCounts = async () => {
    try {
      setCreatingCounts(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pi-counts/schedule/create-todays-counts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        // Refresh status
        await loadScheduleStatus();
        await checkTodaySchedule();
        onClose();
      } else {
        toast.error(data.detail || 'Failed to create counts');
      }
    } catch (error) {
      console.error('Error creating counts:', error);
      toast.error('Failed to create scheduled counts');
    } finally {
      setCreatingCounts(false);
    }
  };

  const getCellContent = (category, week, day) => {
    if (!organized || !organized[category]) return null;
    
    const entries = organized[category][week]?.[day] || [];
    if (entries.length === 0) return null;
    
    // Return structured data for the cell
    return entries;
  };
  
  // Get supplier group names for display
  const getSupplierGroupNames = (groupIds) => {
    return groupIds.map(id => {
      const group = supplierGroups.find(sg => sg.id === id);
      return group ? group.name : null;
    }).filter(Boolean);
  };

  // Render a single supplier group row (like Settings)
  const renderSupplierGroupRow = (sg) => {
    const isSelected = selectedSupplierGroups.includes(sg.id);
    const isScheduled = scheduledSupplierGroupIds.has(sg.id);
    
    return (
      <div
        key={sg.id}
        onClick={() => toggleSupplierGroup(sg.id)}
        className={`flex items-center gap-3 p-3 border-b cursor-pointer transition-all hover:bg-gray-50 ${
          isSelected ? 'bg-purple-50' : ''
        } ${isScheduled && viewAll ? 'opacity-60' : ''}`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          className="w-4 h-4 accent-purple-500"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">{sg.name}</p>
            <span className={`text-xs px-2 py-0.5 rounded ${
              sg.category?.toLowerCase() === 'structural'
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {sg.category}
            </span>
            {isScheduled && viewAll && (
              <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">
                Scheduled
              </span>
            )}
          </div>
          
          {/* Show individual prefixes as tags */}
          {sg.prefixes && sg.prefixes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {sg.prefixes.map((prefix, idx) => (
                <span 
                  key={idx}
                  className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200"
                >
                  {prefix}
                </span>
              ))}
            </div>
          )}
          
          {/* Show sample product codes if available */}
          {sg.sample_products && sg.sample_products.length > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              <span className="font-medium">Products: </span>
              {sg.sample_products.slice(0, 3).map(p => p.code).join(', ')}
              {sg.sample_products.length > 3 && ` +${sg.sample_products.length - 3} more`}
            </div>
          )}
        </div>
        {isSelected && (
          <Check className="w-5 h-5 text-purple-600" />
        )}
      </div>
    );
  };

  // Generate calendar grid for the selected month
  const workingDaysGrid = useMemo(() => {
    return getWorkingDaysGrid(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  const renderScheduleTable = (category, bgColor, borderColor, headerColor) => (
    <div className="flex-1">
      <h3 className={`text-lg font-semibold mb-2 ${headerColor}`}>{category}</h3>
      <table className="w-full border-collapse">
        <thead>
          <tr className={bgColor}>
            <th className={`border ${borderColor} px-2 py-1 text-xs font-semibold w-12`}></th>
            {DAY_NAMES.map(day => (
              <th key={day} className={`border ${borderColor} px-2 py-1 text-xs font-semibold`}>
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workingDaysGrid.map((week, weekIndex) => (
            <tr key={weekIndex}>
              <td className={`border ${borderColor} px-2 py-2 text-center text-xs font-semibold ${bgColor}`}>
                W{weekIndex + 1}
              </td>
              {week.map((dayInfo, dayIndex) => {
                const entries = getCellContent(category, weekIndex + 1, dayIndex);
                const hasContent = entries !== null && entries.length > 0;
                
                // Get supplier group names and all prefixes
                const groupNames = hasContent 
                  ? entries.flatMap(e => getSupplierGroupNames(e.supplier_group_ids || []))
                  : [];
                const allPrefixes = hasContent 
                  ? entries.flatMap(e => e.prefixes || [])
                  : [];
                
                return (
                  <td
                    key={dayIndex}
                    className={`border ${borderColor} p-1 text-left text-xs cursor-pointer hover:bg-opacity-50 transition-colors relative align-top ${
                      hasContent ? bgColor : 'bg-white'
                    } ${!dayInfo.isCurrentMonth ? 'opacity-40' : ''} ${dayInfo.isToday ? 'ring-2 ring-orange-400 ring-inset' : ''}`}
                    onClick={() => handleCellClick(category, weekIndex + 1, dayIndex, dayInfo)}
                    title={`${dayInfo.dayOfMonth} ${MONTH_NAMES[dayInfo.date.getMonth()]} - ${hasContent ? 'Click to edit' : 'Click to add schedule'}`}
                    style={{ minHeight: '90px', width: '18%', verticalAlign: 'top' }}
                  >
                    <span className="absolute top-0.5 right-1 text-[10px] text-gray-400 font-bold bg-white/70 px-1 rounded">
                      {dayInfo.dayOfMonth}
                    </span>
                    {/* Friday Discrepancy Label */}
                    {dayIndex === 4 && (
                      <div className="absolute top-0.5 left-1 text-[8px] font-bold text-red-600 bg-red-50 px-1 rounded">
                        DISCREPANCY
                      </div>
                    )}
                    {hasContent ? (
                      <div className={`${dayIndex === 4 ? 'mt-5' : 'mt-4'} space-y-0.5`}>
                        {/* Supplier Group Names */}
                        {groupNames.slice(0, 3).map((name, idx) => (
                          <div key={idx} className="text-[10px] font-medium truncate leading-tight" title={name}>
                            {name}
                          </div>
                        ))}
                        {groupNames.length > 3 && (
                          <div className="text-[9px] text-gray-500">+{groupNames.length - 3} more</div>
                        )}
                        {/* Prefixes line */}
                        {allPrefixes.length > 0 && (
                          <div className="text-[9px] text-gray-500 mt-1 border-t pt-0.5 truncate" title={allPrefixes.join(', ')}>
                            {allPrefixes.slice(0, 4).join(', ')}{allPrefixes.length > 4 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`${dayIndex === 4 ? 'mt-5' : 'mt-4'} text-gray-300 text-center`}>-</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="mb-6 bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="w-6 h-6" />
            Count Schedule
          </h2>
          
          {/* Month Navigation */}
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1">
            <button
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear(selectedYear - 1);
                } else {
                  setSelectedMonth(selectedMonth - 1);
                }
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronDown className="w-4 h-4 rotate-90" />
            </button>
            <span className="font-medium text-sm min-w-[120px] text-center">
              {MONTH_NAMES[selectedMonth]} {selectedYear}
            </span>
            <button
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear(selectedYear + 1);
                } else {
                  setSelectedMonth(selectedMonth + 1);
                }
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
          </div>
          
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            4-week rolling schedule
          </span>
        </div>
        
        {/* Today's Status */}
        {todaySchedule && todaySchedule.is_working_day && (
          <div className="flex items-center gap-4">
            {/* Auto-run toggle */}
            <button
              onClick={() => toggleAutoRun(!autoRunEnabled)}
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm border transition-all cursor-pointer ${
                autoRunEnabled 
                  ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' 
                  : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
              }`}
              title={autoRunEnabled ? 'Auto-run is ON - Click to disable' : 'Auto-run is OFF - Click to enable'}
            >
              {autoRunEnabled ? (
                <ToggleRight className="w-5 h-5 text-blue-600" />
              ) : (
                <ToggleLeft className="w-5 h-5 text-gray-400" />
              )}
              <Clock className="w-4 h-4" />
              <span>{autoRunEnabled ? 'Auto-run ON (6 AM GMT)' : 'Auto-run OFF'}</span>
            </button>
            
            {/* Free stock status */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
              freeStockUpdatedToday ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {freeStockUpdatedToday ? (
                <>
                  <Check className="w-4 h-4" />
                  Free Stock Updated Today
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  Free Stock Not Updated
                </>
              )}
            </div>
            
            {/* Manual run button - shows when auto-run is off OR as backup */}
            {todaySchedule.schedules?.length > 0 && (
              <Button
                onClick={handleCreateTodaysCounts}
                disabled={!freeStockUpdatedToday || creatingCounts || (scheduleStatus?.counts_created_today > 0)}
                className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                title={scheduleStatus?.counts_created_today > 0 
                  ? "Today's counts already created" 
                  : autoRunEnabled 
                    ? "Manually create today's scheduled counts (backup if auto-run missed)"
                    : "Create today's scheduled counts (auto-run is disabled)"}
              >
                {creatingCounts ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : scheduleStatus?.counts_created_today > 0 ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Created ({scheduleStatus.counts_created_today})
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Run Today's Counts ({todaySchedule.schedules.length})
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading schedule...</div>
      ) : (
        <>
          {/* Schedule Tables */}
          <div className="flex gap-6">
            {renderScheduleTable('Structural', 'bg-blue-50', 'border-blue-200', 'text-blue-700')}
            {renderScheduleTable('Industrial', 'bg-green-50', 'border-green-200', 'text-green-700')}
          </div>

          {/* Group Supplier Groups Button */}
          <div className="mt-4 flex justify-center">
            <Button
              onClick={() => {
                setShowGroupModal(true);
                setSearchQuery('');
                setViewAll(false);
                setSelectedSupplierGroups([]);
              }}
              variant="outline"
              className="border-2 border-purple-400 text-purple-600 hover:bg-purple-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Group Supplier Groups
            </Button>
          </div>
        </>
      )}

      {/* Cell Edit Modal */}
      <Dialog open={showCellModal} onOpenChange={setShowCellModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Edit Schedule - {selectedCell?.category} Week {selectedCell?.week} {DAY_NAMES[selectedCell?.day]}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Search and View All Toggle */}
            <div className="flex gap-3 items-center mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, prefix, product code, or product name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <button
                onClick={() => setViewAll(!viewAll)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all whitespace-nowrap ${
                  viewAll 
                    ? 'border-purple-500 bg-purple-50 text-purple-700' 
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {viewAll ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                <span className="text-sm font-medium">View All</span>
              </button>
            </div>

            {/* Supplier Groups List - Simple scrollable list like Settings */}
            <div className="border-2 rounded-lg overflow-hidden flex-1 flex flex-col">
              <div className="overflow-y-auto flex-1" style={{ maxHeight: '350px' }}>
                {getFilteredByCategory(selectedCell?.category).length > 0 ? (
                  getFilteredByCategory(selectedCell?.category).map(sg => renderSupplierGroupRow(sg))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Search className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No supplier groups found for {selectedCell?.category}</p>
                    {!viewAll && supplierGroups.length > 0 && (
                      <p className="text-sm mt-1">Try enabling "View All" to see scheduled groups</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Selected count */}
            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mt-3">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-purple-600">{selectedSupplierGroups.length}</span> supplier group(s) selected
              </p>
              {selectedSupplierGroups.length > 0 && (
                <button 
                  onClick={() => setSelectedSupplierGroups([])}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Clear all
                </button>
              )}
            </div>
            
            {/* Selected Prefixes Display */}
            {selectedSupplierGroups.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-3">
                <p className="text-xs font-semibold text-purple-700 mb-2">Selected Prefixes:</p>
                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    const selectedPrefixes = supplierGroups
                      .filter(sg => selectedSupplierGroups.includes(sg.id))
                      .flatMap(sg => (sg.prefixes || []).map(p => ({ prefix: p, groupId: sg.id, groupName: sg.name })));
                    
                    return selectedPrefixes.map((item, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-800 border border-purple-300"
                        title={`From: ${item.groupName}`}
                      >
                        {item.prefix}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Remove this supplier group if clicking X on a prefix
                            setSelectedSupplierGroups(prev => prev.filter(id => id !== item.groupId));
                          }}
                          className="ml-0.5 text-purple-500 hover:text-purple-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ));
                  })()}
                </div>
                <p className="text-[10px] text-purple-600 mt-2">
                  Click X on a prefix to remove its supplier group
                </p>
              </div>
            )}

            {/* Date info & Friday Discrepancy note */}
            {selectedCell?.dateInfo && (
              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded mt-3">
                <div className="flex items-center justify-between">
                  <span>
                    Selected date: <span className="font-medium">{selectedCell.dateInfo.dayOfMonth} {MONTH_NAMES[selectedCell.dateInfo.date.getMonth()]} {selectedCell.dateInfo.date.getFullYear()}</span>
                    {!selectedCell.dateInfo.isCurrentMonth && <span className="text-orange-600 ml-2">(Rollover day)</span>}
                  </span>
                </div>
                {selectedCell.day === 4 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                    <span className="font-bold">Friday - Discrepancy Day:</span> The discrepancy count for this week will be automatically sent to the warehouse.
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-3">
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingEdit({ type: 'delete', cell: selectedCell });
                  setShowEditTypeModal(true);
                }}
                className="bg-red-100 text-red-700 hover:bg-red-200 border-0"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Slot
              </Button>
              
              <div className="flex gap-2">
                {/* Add Count Now button - only show if there are selected supplier groups */}
                {selectedSupplierGroups.length > 0 && freeStockUpdatedToday && (
                  <Button 
                    onClick={handleCreateCountFromSchedule}
                    disabled={creatingCountFromSchedule}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {creatingCountFromSchedule ? (
                      <>
                        <span className="animate-spin mr-2">◌</span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Count Now
                      </>
                    )}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowCellModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveCellWithType}
                  disabled={selectedSupplierGroups.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Supplier Groups Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Group Supplier Groups
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col flex-1 overflow-hidden">
            <p className="text-sm text-gray-600 mb-3">
              Select supplier groups from the list below, then click on a cell in the schedule to assign them.
            </p>

            {/* Search and View All Toggle */}
            <div className="flex gap-3 items-center mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, prefix, product code, or product name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <button
                onClick={() => setViewAll(!viewAll)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all whitespace-nowrap ${
                  viewAll 
                    ? 'border-purple-500 bg-purple-50 text-purple-700' 
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {viewAll ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                <span className="text-sm font-medium">View All</span>
              </button>
            </div>

            {/* Supplier Groups List - Simple scrollable list like Settings */}
            <div className="border-2 rounded-lg overflow-hidden flex-1 flex flex-col">
              <div className="overflow-y-auto flex-1" style={{ maxHeight: '400px' }}>
                {filteredSupplierGroups.length > 0 ? (
                  filteredSupplierGroups.map(sg => renderSupplierGroupRow(sg))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Search className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No supplier groups found</p>
                    {!viewAll && supplierGroups.length > 0 && (
                      <p className="text-sm mt-1">Try enabling "View All" to see scheduled groups</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Groups Summary */}
            {selectedSupplierGroups.length > 0 && (
              <div className="bg-purple-50 p-3 rounded-lg border-2 border-purple-200 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-purple-700">
                    Selected ({selectedSupplierGroups.length}):
                  </p>
                  <button 
                    onClick={() => setSelectedSupplierGroups([])}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedSupplierGroups.map(id => {
                    const sg = supplierGroups.find(s => s.id === id);
                    return (
                      <span
                        key={id}
                        className="px-2 py-1 bg-white border border-purple-300 text-purple-700 rounded text-sm flex items-center gap-1"
                      >
                        {sg?.name}
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-purple-900"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSupplierGroup(id);
                          }}
                        />
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Instruction */}
            {selectedSupplierGroups.length > 0 && (
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-3">
                <p className="text-sm text-yellow-800">
                  <strong>Next:</strong> Close this modal and click on a schedule cell to assign these groups.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowGroupModal(false);
                  setSearchQuery('');
                }}
              >
                {selectedSupplierGroups.length > 0 ? 'Done - Select Schedule Slot' : 'Close'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Auto-Run Enable Confirmation Modal */}
      <Dialog open={showAutoRunConfirmModal} onOpenChange={setShowAutoRunConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Clock className="w-6 h-6 text-blue-600" />
              Enable Auto-Run
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              You are about to enable automatic scheduled count creation at 6:00 AM GMT on working days.
            </p>
            
            {todaySchedule?.schedules?.length > 0 && scheduleStatus?.counts_created_today === 0 && freeStockUpdatedToday && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-medium mb-2">
                  There are {todaySchedule.schedules.length} scheduled count(s) for today that haven't been created yet.
                </p>
                <p className="text-blue-700 text-sm">
                  Would you like to create them now, or wait until the next working day?
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAutoRunConfirmModal(false)}
            >
              Cancel
            </Button>
            
            {todaySchedule?.schedules?.length > 0 && scheduleStatus?.counts_created_today === 0 && freeStockUpdatedToday ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => saveAutoRunSetting(true, false)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  Wait Until Next Day
                </Button>
                <Button
                  onClick={() => saveAutoRunSetting(true, true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Today's Counts Now
                </Button>
              </>
            ) : (
              <Button
                onClick={() => saveAutoRunSetting(true, false)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Enable Auto-Run
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Type Modal - One-off or All Future Months */}
      <Dialog open={showEditTypeModal} onOpenChange={setShowEditTypeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6 text-purple-600" />
              {pendingEdit?.type === 'delete' ? 'Remove Schedule Entry' : 'Save Schedule Entry'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              {pendingEdit?.type === 'delete' 
                ? 'How would you like to remove this schedule entry?'
                : 'How would you like to apply this schedule change?'}
            </p>
            
            {pendingEdit?.cell?.dateInfo && (
              <div className="bg-gray-50 border rounded-lg p-3 mb-4 text-sm">
                <p><strong>Date:</strong> {pendingEdit.cell.dateInfo.dayOfMonth} {MONTH_NAMES[pendingEdit.cell.dateInfo.date.getMonth()]} {pendingEdit.cell.dateInfo.date.getFullYear()}</p>
                <p><strong>Position:</strong> Week {pendingEdit.cell.week}, {DAY_NAMES[pendingEdit.cell.day]}</p>
                <p><strong>Category:</strong> {pendingEdit.cell.category}</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={() => {
                if (pendingEdit?.type === 'delete') {
                  handleDeleteCell(false);
                } else {
                  handleSaveCell(false);
                }
              }}
              variant="outline"
              className="w-full justify-start border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <Calendar className="w-4 h-4 mr-2" />
              One-off edit (this date only)
            </Button>
            
            <Button
              onClick={() => {
                if (pendingEdit?.type === 'delete') {
                  handleDeleteCell(true);
                } else {
                  handleSaveCell(true);
                }
              }}
              className="w-full justify-start bg-purple-600 hover:bg-purple-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Apply to all future months (rolling schedule)
            </Button>
            
            <Button
              variant="ghost"
              onClick={() => setShowEditTypeModal(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PICountSchedule;
